import { exportToCanvas } from "@excalidraw/excalidraw";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
  NonDeleted,
} from "@excalidraw/excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

export const A4_MM = { long: 297, short: 210 } as const;
export const PDF_MARGIN_MM = 10;

/** Placement of one frame image on an A4 page, in millimetres. */
export type PageLayout = {
  orientation: "landscape" | "portrait";
  x: number;
  y: number;
  w: number;
  h: number;
};

/** Longest side of a rendered page image: A4 long side at 300 dpi. */
const PAGE_IMAGE_PX = 2480;

export type FramePdfSource = {
  elements: readonly ExcalidrawElement[];
  appState: Partial<AppState>;
  files: BinaryFiles;
};

/** A square frame counts as landscape. The image is fitted inside the margin
 * with its ratio kept and centred, so nothing is cropped or stretched. */
export const pageLayout = (
  frameWidth: number,
  frameHeight: number,
): PageLayout => {
  const landscape = frameWidth >= frameHeight;
  const pageW = landscape ? A4_MM.long : A4_MM.short;
  const pageH = landscape ? A4_MM.short : A4_MM.long;
  const boxW = pageW - PDF_MARGIN_MM * 2;
  const boxH = pageH - PDF_MARGIN_MM * 2;
  const scale = Math.min(boxW / frameWidth, boxH / frameHeight);
  const w = frameWidth * scale;
  const h = frameHeight * scale;
  return {
    orientation: landscape ? "landscape" : "portrait",
    x: (pageW - w) / 2,
    y: (pageH - h) / 2,
    w,
    h,
  };
};

const pad = (value: number) => String(value).padStart(2, "0");

export const pdfFileName = (
  canvasName: string | null | undefined,
  date: Date = new Date(),
): string => {
  const name =
    (canvasName ?? "")
      // eslint-disable-next-line no-control-regex
      .replace(/[\\/:*?"<>|\u0000-\u001f\u007f]/g, " ")
      .replace(/\s+/g, " ")
      .trim() || "캔버스";
  const stamp = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(
    date.getDate(),
  )}`;
  return `${name}-${stamp}.pdf`;
};

/** Reading order: top to bottom, then left to right. */
export const sortFramesForReading = <T extends { x: number; y: number }>(
  frames: readonly T[],
): T[] => [...frames].sort((a, b) => a.y - b.y || a.x - b.x);

export const renderFrameCanvas = (
  source: FramePdfSource,
  frame: ExcalidrawFrameLikeElement,
  longSide: number,
): Promise<HTMLCanvasElement> =>
  exportToCanvas({
    elements: source.elements.filter(
      (element) => !element.isDeleted,
    ) as NonDeleted<ExcalidrawElement>[],
    appState: {
      ...source.appState,
      exportBackground: true,
      exportWithDarkMode: false,
      viewBackgroundColor: source.appState.viewBackgroundColor || "#ffffff",
    },
    files: source.files,
    exportingFrame: frame,
    // `maxWidthOrHeight` only ever scales DOWN, which would leave a small
    // frame at screen resolution. Fix the long side exactly instead.
    getDimensions: (width, height) => {
      const scale = longSide / Math.max(width, height);
      return {
        width: Math.round(width * scale),
        height: Math.round(height * scale),
        scale,
      };
    },
  });

export const exportFramesToPdf = async ({
  source,
  frames,
  fileName,
  onProgress,
}: {
  source: FramePdfSource;
  frames: readonly ExcalidrawFrameLikeElement[];
  fileName: string;
  onProgress?: (done: number, total: number) => void;
}): Promise<void> => {
  if (frames.length === 0) {
    throw new Error("No frames to export");
  }
  // Loaded on demand so the PDF library stays out of the main bundle.
  const { jsPDF } = await import("jspdf");
  const first = pageLayout(frames[0].width, frames[0].height);
  const doc = new jsPDF({
    orientation: first.orientation,
    unit: "mm",
    format: "a4",
  });
  for (let index = 0; index < frames.length; index++) {
    const frame = frames[index];
    const layout = pageLayout(frame.width, frame.height);
    const canvas = await renderFrameCanvas(source, frame, PAGE_IMAGE_PX);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    if (index > 0) {
      doc.addPage("a4", layout.orientation);
    }
    doc.addImage(dataUrl, "JPEG", layout.x, layout.y, layout.w, layout.h);
    onProgress?.(index + 1, frames.length);
  }
  doc.save(fileName);
};
